import type { CityConfig } from "../city-template/types";

export const erlangerConfig: CityConfig = {
  city: "Erlanger",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "erlanger",
  route: "/cities/erlanger",
  title: "Emergency Passport Erlanger, KY | Fast Passport Renewal | CVG Airport Area",
  metaDescription: "Emergency passport and expedited passport services in Erlanger, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Kenton County, Northern Kentucky, and CVG airport area travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Erlanger, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Erlanger and Kenton County travelers get their expedited passports as quickly as 24 hours. Serving the CVG airport corridor, retail corridor along Dixie Highway, and the entire Northern Kentucky community. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Erlanger%20Kentucky%20suburban%20commercial%20area%20with%20modern%20retail%20developments%20and%20wide%20boulevards%2C%20suburban%20Northern%20Kentucky%20community%20with%20green%20trees%20and%20well-maintained%20streets%2C%20clean%20suburban%20photography%20with%20warm%20daylight%2C%20no%20visible%20text%20or%20people%2C%20residential%20neighborhoods%20mixed%20with%20commercial%20areas%2C%20wide%20panoramic%20view&width=1800&height=700&seq=erlanger-hero-001&orientation=landscape",
  heroImageAlt: "Erlanger Kentucky suburban commercial area with modern retail developments and residential neighborhoods",
  geoPosition: "39.0167;-84.6008",
  geoPlacename: "Erlanger, Kentucky",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "77 W Jackson Blvd, Suite 1200, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 310 miles from Erlanger via I-74 and I-65",
  },
  nearbyCitiesZips: [
    { name: "Erlanger", zips: "41018, 41017, 41051" },
    { name: "Florence", zips: "41022, 41042, 41043, 41044" },
    { name: "Independence", zips: "41051, 41052, 41053, 41054, 41055, 41056, 41057, 41058, 41059, 41060, 41061, 41062, 41063, 41064, 41065, 41066, 41067, 41068, 41069, 41070, 41071, 41072, 41073, 41074, 41075, 41076, 41077, 41078, 41079, 41080, 41081, 41082, 41083, 41084, 41085, 41086, 41087, 41088, 41089, 41090, 41091, 41092, 41093, 41094, 41095, 41096, 41097, 41098, 41099" },
    { name: "Covington", zips: "41011, 41012, 41014, 41015, 41016, 41017, 41018, 41019" },
    { name: "Fort Mitchell", zips: "41011, 41017, 41051" },
    { name: "Edgewood", zips: "41017, 41018, 41051" },
    { name: "Fort Thomas", zips: "41075, 41076" },
    { name: "Newport", zips: "41071, 41072, 41075, 41076, 41099" },
    { name: "Bellevue", zips: "41073, 41074" },
    { name: "Union", zips: "41091, 41042" },
    { name: "Hebron", zips: "41048, 41005, 41042" },
    { name: "Walton", zips: "41094, 41091, 41042" },
    { name: "Taylor Mill", zips: "41015, 41017, 41051" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Kenton County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Florence", route: "/cities/florence" },
    { name: "Fast passport renewal in Covington", route: "/cities/covington" },
    { name: "Emergency passport help in Louisville", route: "/cities/louisville" },
    { name: "Urgent passport services in Lexington", route: "/cities/lexington" },
    { name: "Same-day passport help in Fort Thomas", route: "/cities/fort-thomas" },
    { name: "Passport courier services near Independence", route: "/cities/independence" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Erlanger remotely via secure courier. We do not have a walk-in office in Erlanger — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Kenton County travelers.",
  fpcOfficeDistance: "Serving all of Kenton County and the Northern Kentucky CVG airport corridor",
  serviceAreaIntro: "From the retail corridor along Dixie Highway to the quiet neighborhoods near the airport, from the Thomas More University area to the growing suburban communities — Fast Passport Center serves the entire Erlanger community. We regularly help travelers from Florence, Independence, and Fort Mitchell get their passports fast.",
  nearbyCitiesIntro: "We serve all Erlanger neighborhoods and nearby Kenton County cities including Florence, Independence, Fort Mitchell, Edgewood, Covington, Fort Thomas, Newport, and Bellevue. If you are in Kenton County, Northern Kentucky, or the CVG airport corridor, we have you covered.",
  howItWorksIntro: "Most Erlanger travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Erlanger travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Erlanger and Kenton County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Erlanger? Here are the most common ones we hear from Kenton County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Kenton County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Michael H.",
      location: "Erlanger, KY",
      rating: 5,
      text: "My wife and I had a trip to Paris planned for our anniversary. Her passport expired 3 weeks before. Fast Passport Center renewed it in 4 days. We walked along the Seine just as planned. Incredible service for the Erlanger area.",
      service: "Passport Renewal",
    },
    {
      name: "Sarah J.",
      location: "Florence, KY",
      rating: 5,
      text: "Emergency business trip to Frankfurt via CVG in 2 days. My passport was damaged. They got me a replacement in 24 hours. Their courier service is perfect for Northern Kentucky professionals near the airport.",
      service: "Emergency Passport",
    },
    {
      name: "Chris D.",
      location: "Independence, KY",
      rating: 5,
      text: "My teenager needed a passport for a school trip to Japan. Fast Passport Center walked us through the parental consent process and got it done in 6 days. They even found a photo location near Dixie Highway.",
      service: "Child Passport",
    },
    {
      name: "Lisa R.",
      location: "Fort Mitchell, KY",
      rating: 5,
      text: "Lost my passport the night before a family Disney cruise to the Bahamas. I called in a panic and they had a replacement in my hands in 36 hours. We made the cruise from Miami. I cannot thank them enough.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "erlanger-1",
      name: "Erlanger Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3130 Dixie Hwy",
      city: "Erlanger",
      state: "KY",
      zip: "41018",
      phone: "(859) 727-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from Erlanger center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary facility for Kenton County residents. Full passport services available. Appointments recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "erlanger-2",
      name: "Florence Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7100 Turfway Rd",
      city: "Florence",
      state: "KY",
      zip: "41042",
      phone: "(859) 371-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3.5 miles from Erlanger",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Boone County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "erlanger-3",
      name: "Kenton County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "303 Court St",
      city: "Covington",
      state: "KY",
      zip: "41011",
      phone: "(859) 392-1400",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "5.5 miles from Erlanger",
      website: "https://www.kentoncountyky.gov/",
      websiteLabel: "Kenton County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
    {
      id: "erlanger-4",
      name: "Independence Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6250 Taylor Mill Rd",
      city: "Independence",
      state: "KY",
      zip: "41051",
      phone: "(859) 356-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "4.5 miles from Erlanger",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Independence residents. Full passport services available.",
      icon: "ri-mail-line",
    },
    {
      id: "erlanger-5",
      name: "Fort Mitchell Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3400 Highland Ave",
      city: "Fort Mitchell",
      state: "KY",
      zip: "41017",
      phone: "(859) 341-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3.0 miles from Erlanger",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Kenton County residents. Convenient for Fort Mitchell area.",
      icon: "ri-mail-line",
    },
    {
      id: "erlanger-6",
      name: "Edgewood Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3358 Dixie Hwy",
      city: "Edgewood",
      state: "KY",
      zip: "41017",
      phone: "(859) 331-1511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.0 miles from Erlanger",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Edgewood and nearby Kenton County residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Cincinnati metro photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Kenton County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Erlanger applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Kenton County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Northern Kentucky travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Erlanger." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Erlanger address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Erlanger before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Erlanger for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 5 hour drive to Chicago." },
    { q: "How fast can I renew my passport in Erlanger?", a: "Fast Passport Center offers fast passport renewal in Erlanger in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Erlanger in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Erlanger?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency — a 5 hour drive from Erlanger — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Kenton County residents." },
    { q: "Can I expedite a child passport in Erlanger?", a: "Yes. Fast Passport Center expedites child passports in Erlanger for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Kenton County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Erlanger with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Erlanger?", a: "With Fast Passport Center's emergency courier service, Erlanger travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Erlanger?", a: "Popular options include the Erlanger Post Office (41018), Florence Post Office (41042), Kenton County Clerk (41011), Independence Post Office (41051), Fort Mitchell Post Office (41017), and Edgewood Post Office (41017). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Erlanger?", a: "Same-day passports are only available at the Chicago Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the 5 hour drive to Chicago." },
    { q: "What if I live near CVG airport?", a: "We serve the entire CVG airport corridor including Erlanger, Florence, Hebron, Union, and Independence. Our overnight shipping labels work from any UPS location, so your location near the airport doesn't affect processing speed. Many CVG-based airline employees and frequent travelers rely on our service." },
    { q: "How much does expedited passport service cost in Erlanger?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Erlanger?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and Walmart locations in Erlanger, Florence, and Covington offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Erlanger?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Erlanger?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Kenton County families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "Walmart — Erlanger", address: "3100 Dixie Hwy, Erlanger, KY 41018", hours: "Mon–Sun: 6AM–11PM" },
    { name: "CVS Pharmacy — Erlanger", address: "3185 Dixie Hwy, Erlanger, KY 41018", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Florence", address: "7625 Doering Dr, Florence, KY 41042", hours: "Mon–Sun: 6AM–11PM" },
    { name: "CVS Pharmacy — Florence", address: "6900 Burlington Pike, Florence, KY 41042", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Fort Mitchell", address: "2501 Dixie Hwy, Fort Mitchell, KY 41017", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Covington", address: "25 W 5th St, Covington, KY 41011", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Independence", address: "6250 Taylor Mill Rd, Independence, KY 41051", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Florence", address: "4990 Houston Rd, Florence, KY 41042", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Kenton County",
      address: "303 Court St, Covington, KY 41011",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Boone County",
      address: "2950 Washington St, Burlington, KY 41005",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for Boone County residents.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Campbell County",
      address: "330 York St, Newport, KY 41071",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Online booking available.",
      featured: false,
    },
    {
      name: "Ohio BMV — Cincinnati",
      address: "5721 Dragon Way, Cincinnati, OH 45227",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Alternative for Ohio residents across the river. Walk-ins limited.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "CVG",
      name: "Cincinnati/Northern Kentucky International",
      distance: "5 miles from Erlanger",
      address: "3087 Terminal Dr, Hebron, KY 41048",
      note: "The primary airport for Erlanger and Kenton County. Domestic and international flights to major hubs worldwide.",
      flightUrl: "https://www.cvgairport.com/",
      flightLabel: "CVG Airport",
      featured: true,
    },
    {
      code: "SDF",
      name: "Louisville Muhammad Ali International",
      distance: "105 miles from Erlanger",
      address: "600 Terminal Dr, Louisville, KY 40209",
      note: "Alternative regional airport with flights to major hubs.",
      flightUrl: "https://www.flylouisville.com/",
      flightLabel: "Louisville Airport",
      featured: false,
    },
    {
      code: "LEX",
      name: "Blue Grass Airport (Lexington)",
      distance: "90 miles from Erlanger",
      address: "4000 Terminal Dr, Lexington, KY 40510",
      note: "Alternative regional airport for central Kentucky flights.",
      flightUrl: "https://www.bluegrassairport.com/",
      flightLabel: "Blue Grass Airport",
      featured: false,
    },
  ],
};